import apiClient, { setAuthToken } from './apiClient';
import type { InsightListResponse, PackageType, UserRole, WeeklyInsight } from '@/types';

// ----------------------------------------------------------------------
// UPLOADS
// ----------------------------------------------------------------------
export const uploadService = {
  uploadClubLogo: async (file: File, clubId: number) =>
    apiClient.request(`/clubs/upload-logo?club_id=${encodeURIComponent(String(clubId))}`, {
      method: 'POST',
      body: (() => {
        const formData = new FormData();
        formData.append('file', file);
        return formData;
      })(),
    }) as Promise<{ url: string }>,
};

// Utilities -------------------------------------------------------------
const toBackendPackage = (pkg?: PackageType) => {
  if (!pkg) return undefined;
  return pkg.toUpperCase(); // "package1" -> "PACKAGE1"
};

const toBackendRole = (role?: UserRole | string) => {
  if (!role) return undefined;
  const r = String(role).toLowerCase();
  if (r === 'player' || r === 'member') return 'PLAYER';
  if (r === 'coach') return 'COACH';
  if (r === 'admin') return 'ADMIN';
  return String(role).toUpperCase();
};

// ----------------------------------------------------------------------
// AUTH / USERS
// ----------------------------------------------------------------------
export const userService = {
  // ✅ OTP: request code
  requestOtp: async (data: { email: string; first_name?: string; last_name?: string }) =>
    apiClient.request('/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
      }),
    }) as Promise<{ otp_session_id: number }>,

  // ✅ OTP: verify code
  verifyOtp: async (data: { otp_session_id: number; otp: string }) =>
    apiClient.request('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({
        otp_session_id: data.otp_session_id,
        otp: data.otp,
      }),
    }) as Promise<{ verified: boolean }>,

  // ✅ UPDATED SIGNUP (now requires otp_session_id)
  signup: async (data: {
    email: string;
    password: string;

    // support old callers
    firstName?: string;
    lastName?: string;
    packageType?: PackageType;

    // support new callers (snake_case)
    first_name?: string;
    last_name?: string;
    package?: string;
    club_name?: string;
    sport_type?: string;
    is_yearly_plan?: boolean;
    yearly_discount_pct?: number;
    yearly_price_at_signup?: number;

    // Optional when backend OTP bypass is enabled
    otp_session_id?: number;
    accept_privacy_policy?: boolean;
    accept_data_collection?: boolean;
    accept_terms_and_conditions?: boolean;
  }) => {
    const first_name = data.first_name ?? data.firstName;
    const last_name = data.last_name ?? data.lastName;

    // allow either already formatted package or frontend enum
    const pkg = data.package ?? toBackendPackage(data.packageType);

    return apiClient.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({
        email: data.email,
        password: data.password,
        first_name,
        last_name,
        package: pkg,
        club_name: data.club_name,
        sport_type: data.sport_type,
        is_yearly_plan: Boolean(data.is_yearly_plan),
        yearly_discount_pct: data.yearly_discount_pct,
        yearly_price_at_signup: data.yearly_price_at_signup,
        otp_session_id: data.otp_session_id, // ✅ REQUIRED
        accept_privacy_policy: Boolean(data.accept_privacy_policy),
        accept_data_collection: Boolean(data.accept_terms_and_conditions ?? data.accept_data_collection),
        accept_terms_and_conditions: Boolean(data.accept_terms_and_conditions ?? data.accept_data_collection),
      }),
    });
  },

  login: async (email: string, password: string) =>
    apiClient.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  verifyLoginOtp: async (data: { otp_session_id: number; otp: string }) =>
    apiClient.request('/auth/login/verify-otp', {
      method: 'POST',
      body: JSON.stringify({
        otp_session_id: data.otp_session_id,
        otp: data.otp,
      }),
    }) as Promise<{ access_token: string; token_type: 'bearer' }>,

  requestPasswordReset: async (email: string) =>
    apiClient.request('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }) as Promise<{ message: string }>,

  me: async (token?: string | null) => {
    if (token) setAuthToken(token);
    return apiClient.request('/auth/me');
  },
  acceptRequiredConsents: async (data?: { source?: string; session_id?: string }) =>
    apiClient.request('/auth/consent/accept', {
      method: 'POST',
      body: JSON.stringify({
        source: data?.source ?? 'consent_gate',
        session_id: data?.session_id,
      }),
    }),

  adminAddMember: async (data: {
    clubId: number | string;
    email: string;
    firstName: string;
    lastName: string;
    nickname?: string | null;
    role: string;
  }) =>
    apiClient.request('/auth/admin/add-member', {
      method: 'POST',
      body: JSON.stringify({
        club_id: Number(data.clubId),
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        nickname: data.nickname,
        role: toBackendRole(data.role),
      }),
    }),

  adminAddMembersBulk: async (data: {
    clubId: number | string;
    role: 'player' | 'coach';
    rows: Array<{ firstName: string; lastName: string; email: string }>;
  }) =>
    apiClient.request('/auth/admin/add-members-bulk', {
      method: 'POST',
      body: JSON.stringify({
        club_id: Number(data.clubId),
        role: toBackendRole(data.role),
        rows: data.rows.map((row) => ({
          first_name: row.firstName,
          last_name: row.lastName,
          email: row.email,
        })),
      }),
    }) as Promise<{
      message: string;
      requested_count: number;
      added_count: number;
      not_added_count: number;
      package_code: string;
      max_active_members: number;
      active_members_before: number;
      active_members_after: number;
      partial: boolean;
      not_added: Array<{ row: number; email?: string | null; reason: string; message?: string }>;
    }>,

  addToClub: async (clubId: number | string, data: { memberId: number; role: 'PLAYER' | 'COACH' }) =>
    apiClient.request(`/clubs/${clubId}/members/add`, {
      method: 'POST',
      body: JSON.stringify({
        member_id: data.memberId,
        role: data.role,
      }),
    }),

  firstTimeLogin: async (
    token: string,
    password: string,
    packageType?: PackageType,
    acceptPrivacyPolicy = true,
    acceptTermsAndConditions = true,
  ) =>
    apiClient.request('/auth/first-time-login', {
      method: 'POST',
      body: JSON.stringify({
        token,
        password,
        package: toBackendPackage(packageType),
        accept_privacy_policy: acceptPrivacyPolicy,
        accept_data_collection: acceptTermsAndConditions,
        accept_terms_and_conditions: acceptTermsAndConditions,
      }),
    }),

  validateFirstTimeToken: async (token: string) =>
    apiClient.request(`/auth/first-time-login/validate?token=${encodeURIComponent(token)}`),
};

export const billingService = {
  getStatus: async (clubId?: number | null) =>
    apiClient.request(clubId ? `/billing/status?club_id=${encodeURIComponent(String(clubId))}` : '/billing/status') as Promise<{
    stripe_customer_id?: string | null;
    package?: string | null;
    billing_cycle?: 'monthly' | 'yearly' | null;
    status?: string | null;
    stripe_subscription_id?: string | null;
    current_period_start?: string | null;
    current_period_end?: string | null;
    trial_start?: string | null;
    trial_end?: string | null;
    cancel_at_period_end?: boolean | null;
    recommended_package?: string | null;
    recommended_billing_cycle?: 'monthly' | 'yearly' | null;
    invoices?: Array<{
      stripe_invoice_id: string;
      amount_due?: number | null;
      amount_paid?: number | null;
      currency?: string | null;
      status?: string | null;
      hosted_invoice_url?: string | null;
      paid_at?: string | null;
      created_at?: string | null;
    }>;
  }>,
  createCheckoutSession: async (data: { package?: string; billing_cycle?: 'monthly' | 'yearly' }) =>
    apiClient.request('/billing/checkout-session', {
      method: 'POST',
      body: JSON.stringify(data),
    }) as Promise<{ checkout_url: string; session_id: string }>,
  createPortalSession: async () =>
    apiClient.request('/billing/portal-session', { method: 'POST' }) as Promise<{ portal_url: string }>,
  cancelSubscription: async () =>
    apiClient.request('/billing/subscription/cancel', { method: 'POST' }) as Promise<{
      success: boolean;
      status?: string | null;
      cancel_at_period_end?: boolean | null;
      current_period_end?: string | null;
    }>,
};

export const insightsService = {
  listMine: async (clubId: number, limit = 10, offset = 0, lang = 'en') =>
    apiClient.request(
      `/insights/${encodeURIComponent(String(clubId))}/me?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}&lang=${encodeURIComponent(lang)}`,
    ) as Promise<InsightListResponse>,
  generateMine: async (clubId: number, force = false) =>
    apiClient.request('/insights/generate', {
      method: 'POST',
      body: JSON.stringify({
        club_id: clubId,
        force,
      }),
    }) as Promise<WeeklyInsight>,
};

export const superAdminService = {
  getDashboard: async (params?: { package?: string; page?: number; page_size?: number }) => {
    const query = new URLSearchParams();
    if (params?.package) query.set('package', params.package);
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiClient.request(`/super-admin/dashboard${suffix}`) as Promise<{
    total_clubs: number;
    total_members: number;
    active_subscriptions: number;
    package_filter?: string | null;
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    clubs: Array<{
      club_id: number;
      club_name: string;
      sport_type?: string | null;
      total_members: number;
      active_players: number;
      package_code?: string | null;
      billing_cycle?: string | null;
      subscription_status?: string | null;
      last_payment_amount?: number | null;
      last_payment_currency?: string | null;
      last_payment_at?: string | null;
      next_payment_at?: string | null;
      trial_end?: string | null;
      payer_first_name?: string | null;
      payer_last_name?: string | null;
      payer_email?: string | null;
    }>;
  }>;
  },
  getPolicies: async () => apiClient.request('/super-admin/policies') as Promise<{
    policies: Array<{
      policy_type: 'privacy_policy' | 'terms_and_conditions';
      active_version?: string | null;
      active_effective_at?: string | null;
      active_title?: string | null;
      active_content_markdown?: string | null;
      active_content_html?: string | null;
      draft_title?: string | null;
      draft_content_markdown?: string | null;
      draft_content_html?: string | null;
      draft_updated_at?: string | null;
      draft_updated_by_email?: string | null;
    }>;
  }>,
  saveDraft: async (
    policyType: 'privacy_policy' | 'terms_and_conditions',
    data: { title: string; content_markdown?: string; content_html?: string },
  ) =>
    apiClient.request(`/super-admin/policies/${policyType}/draft`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  publishPolicy: async (
    policyType: 'privacy_policy' | 'terms_and_conditions',
    data: { version: string; title: string; content_markdown?: string; content_html?: string; effective_at?: string },
  ) =>
    apiClient.request(`/super-admin/policies/${policyType}/publish`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getAuditLogs: async (params?: { page?: number; page_size?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiClient.request(`/super-admin/audit-logs${suffix}`) as Promise<{
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    items: Array<{
      id: string;
      created_at: string;
      action: string;
      actor_member_id?: number | null;
      actor_email?: string | null;
      target_type?: string | null;
      target_id?: string | null;
      details?: string | null;
    }>;
    }>;
  },
};

export const policyService = {
  getActivePolicy: async (policyType: 'privacy_policy' | 'terms_and_conditions') =>
    apiClient.request(`/policies/active/${policyType}`) as Promise<{
      policy_type: 'privacy_policy' | 'terms_and_conditions';
      version: string;
      title?: string | null;
      content_html?: string | null;
      content_markdown?: string | null;
      effective_at: string;
      published_at?: string | null;
    }>,
};

export const adminService = {
  listPendingSignups: async () => {
    throw new Error('Pending approvals are disabled.');
  },
  approvePendingSignup: async (_pendingId: number) => {
    throw new Error('Pending approvals are disabled.');
  },
};

// ----------------------------------------------------------------------
// CLUBS
// ----------------------------------------------------------------------
export const clubService = {
  list: async () => apiClient.request('/clubs'),
  listMine: async () => apiClient.request('/clubs/my'),
  get: async (clubId: number | string) => apiClient.request(`/clubs/${clubId}`),
  getTheme: async (clubId: number | string) =>
    apiClient.request(`/clubs/${clubId}/theme`) as Promise<{
      club_id: number;
      mode?: 'light' | 'dark' | null;
      menu_bg_color?: string | null;
      menu_font?: string | null;
      app_font?: string | null;
      changed_by_member_id?: number | null;
      updated_at?: string | null;
    }>,
  updateTheme: async (
    clubId: number | string,
    payload: {
      mode?: 'light' | 'dark' | null;
      menu_bg_color?: string | null;
      menu_font?: string | null;
      app_font?: string | null;
    },
  ) =>
    apiClient.request(`/clubs/${clubId}/theme`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }) as Promise<{
      club_id: number;
      mode?: 'light' | 'dark' | null;
      menu_bg_color?: string | null;
      menu_font?: string | null;
      app_font?: string | null;
      changed_by_member_id?: number | null;
      updated_at?: string | null;
    }>,
  create: async (payload: Record<string, unknown>) =>
    apiClient.request('/clubs/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  update: async (clubId: number | string, payload: Record<string, unknown>) =>
    apiClient.request(`/clubs/${clubId}/update`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  delete: async (clubId: number | string) => apiClient.request(`/clubs/${clubId}/delete`, { method: 'DELETE' }),
};

// ----------------------------------------------------------------------
// CLUB MEMBERS
// ----------------------------------------------------------------------
export const memberService = {
  list: async (clubId: number | string) => apiClient.request(`/clubs/${clubId}/members`),

  getCapacity: async (clubId: number | string) =>
    apiClient.request(`/clubs/${clubId}/members/capacity`) as Promise<{
      package_code: string;
      max_active_members: number;
      active_members: number;
      over_limit_by: number;
      is_over_limit: boolean;
    }>,

  applyCapacity: async (clubId: number | string, activeMemberIds: number[]) =>
    apiClient.request(`/clubs/${clubId}/members/capacity/apply`, {
      method: 'POST',
      body: JSON.stringify({ active_member_ids: activeMemberIds }),
    }) as Promise<{
      success: boolean;
      active_members: number;
      max_active_members: number;
      package_code: string;
    }>,

  addToClub: async (clubId: number | string, data: { memberId: number; role: 'PLAYER' | 'COACH' }) =>
    apiClient.request(`/clubs/${clubId}/members/add`, {
      method: 'POST',
      body: JSON.stringify({
        member_id: data.memberId,
        role: data.role,
      }),
    }),

  updateRole: async (clubId: number | string, memberId: number | string, role: 'PLAYER' | 'COACH' | 'ADMIN') =>
    apiClient.request(`/clubs/${clubId}/members/${memberId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  updateDetails: async (
    clubId: number | string,
    memberId: number | string,
    data: { first_name?: string; last_name?: string; nickname?: string; email?: string },
  ) =>
    apiClient.request(`/clubs/${clubId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  remove: async (clubId: number | string, memberId: number | string) =>
    apiClient.request(`/clubs/${clubId}/members/${memberId}/remove`, { method: 'DELETE' }),

  updateStatus: async (clubId: number | string, memberId: number | string, is_active: boolean) =>
    apiClient.request(`/clubs/${clubId}/members/${memberId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active }),
    }),
};

// ----------------------------------------------------------------------
// QUEUE
// ----------------------------------------------------------------------
export const queueService = {
  list: async (clubId: number | string) => apiClient.request(`/clubs/${clubId}/queue`),

  markAvailable: async (clubId: number | string, memberId: number | string) =>
    apiClient.request(`/clubs/${clubId}/queue/mark-available`, {
      method: 'POST',
      body: JSON.stringify({ member_id: Number(memberId) }),
    }),

  remove: async (clubId: number | string, memberId: number | string) =>
    apiClient.request(`/clubs/${clubId}/queue/${memberId}/remove`, { method: 'DELETE' }),

  clear: async (clubId: number | string) => apiClient.request(`/clubs/${clubId}/queue/clear`, { method: 'POST' }),

  shuffle: async (clubId: number | string) =>
    apiClient.request(`/clubs/${clubId}/queue/shuffle`, { method: 'POST' }),

  skipTop: async (clubId: number | string) =>
    apiClient.request(`/clubs/${clubId}/queue/skip-top`, { method: 'POST' }),

  history: async (clubId: number | string, page = 1, pageSize = 12) =>
    apiClient.request(`/clubs/${clubId}/queue/history?page=${page}&page_size=${pageSize}`),
};

// ----------------------------------------------------------------------
// COURT ASSIGNMENT / MATCHES
// ----------------------------------------------------------------------
export const courtService = {
  assignPlayers: async (clubId: number | string, courtId: number | string) =>
    apiClient.request(`/clubs/${clubId}/courts/${courtId}/assign`, { method: 'POST' }),

  removePlayer: async (clubId: number | string, courtId: number | string, memberId: number | string) =>
    apiClient.request(`/clubs/${clubId}/courts/${courtId}/remove-player`, {
      method: 'POST',
      body: JSON.stringify({ member_id: Number(memberId) }),
    }),

  swapPlayers: async (
    clubId: number | string,
    courtId: number | string,
    data: { playerId: number | string; targetPlayerId: number | string },
  ) =>
    apiClient.request(`/clubs/${clubId}/courts/${courtId}/swap-players`, {
      method: 'POST',
      body: JSON.stringify({
        player_id: Number(data.playerId),
        target_player_id: Number(data.targetPlayerId),
      }),
    }),

  assignPlayersPick: async (clubId: number | string, courtId: number | string, playerIds: Array<number | string>) =>
    apiClient.request(`/clubs/${clubId}/courts/${courtId}/assign-players-pick`, {
      method: 'POST',
      body: JSON.stringify({
        player_ids: playerIds.map((id) => Number(id)),
      }),
    }),
};

export const matchService = {
  finish: async (
    matchId: number | string,
    payload: {
      team1_score: string;
      team2_score: string;
      notes?: string;
      players: Array<{ player_id: number; racket_id?: number; shoe_id?: number }>;
    },
  ) =>
    apiClient.request(`/matches/${matchId}/finish`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listHistoryByClub: async (clubId: number | string) => apiClient.request(`/matches/club/${clubId}`),

  listHistoryForUserInClub: async (userId: number | string, clubId: number | string) =>
    apiClient.request(`/matches/history/${userId}?club_id=${clubId}`),

  getLeaderboard: async (clubId: number | string) => apiClient.request(`/leaderboard/club/${clubId}`),

  getLeaderboardHistory: async (clubId: number | string, memberId: number | string, days = 30) =>
    apiClient.request(`/leaderboard/club/${clubId}/history/${memberId}?days=${days}`),

  getHeadToHead: async (payload: { club_id: number; team1_player_ids: number[]; team2_player_ids: number[] }) =>
    apiClient.request('/matches/head-to-head', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getDailyReport: async (clubId: number | string, date: string, tzOffset?: number) => {
    const offset = typeof tzOffset === 'number' ? tzOffset : new Date().getTimezoneOffset();
    return apiClient.request(`/matches/daily-report/${clubId}?date=${date}&tz_offset=${offset}`);
  },
};

// ----------------------------------------------------------------------
// EQUIPMENT
// ----------------------------------------------------------------------
export const equipmentService = {
  listShoes: async () => apiClient.request('/equipment/shoes'),
  listRackets: async () => apiClient.request('/equipment/rackets'),

  addShoe: async (data: { make: string; manufacturer?: string }) =>
    apiClient.request('/equipment/shoes', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateShoe: async (shoeId: number | string, data: { make?: string; manufacturer?: string }) =>
    apiClient.request(`/equipment/shoes/${shoeId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteShoe: async (shoeId: number | string) => apiClient.request(`/equipment/shoes/${shoeId}`, { method: 'DELETE' }),

  addRacket: async (data: { make: string; manufacturer?: string; tension?: string; string_type?: string }) =>
    apiClient.request('/equipment/rackets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRacket: async (
    racketId: number | string,
    data: { make?: string; manufacturer?: string; tension?: string; string_type?: string },
  ) =>
    apiClient.request(`/equipment/rackets/${racketId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteRacket: async (racketId: number | string) => apiClient.request(`/equipment/rackets/${racketId}`, { method: 'DELETE' }),
};

// ----------------------------------------------------------------------
// GOALS & FEEDBACK
// ----------------------------------------------------------------------
export const goalService = {
  assign: async (payload: { player_id: number; club_id: number; goal_text: string; year?: number }) =>
    apiClient.request('/goals/', { method: 'POST', body: JSON.stringify(payload) }),

  update: async (goalId: number | string, payload: { goal_text?: string; is_completed?: boolean }) =>
    apiClient.request(`/goals/${goalId}`, { method: 'PUT', body: JSON.stringify(payload) }),

  remove: async (goalId: number | string) => apiClient.request(`/goals/${goalId}`, { method: 'DELETE' }),

  listForPlayer: async (playerId: number | string, clubId: number | string) =>
    apiClient.request(`/goals/${playerId}?club_id=${clubId}`),

  listForCoach: async (coachId: number | string, clubId: number | string) =>
    apiClient.request(`/goals/coach/${coachId}?club_id=${clubId}`),
};

export const feedbackService = {
  submit: async (payload: {
    club_id: number;
    player_id: number;
    technical_skills?: number;
    movement?: number;
    tactical_ability?: number;
    physical_attributes?: number;
    psychological_factors?: number;
    quarter_plan?: string;
    comments?: string;
  }) =>
    apiClient.request('/coach-evaluations/', { method: 'POST', body: JSON.stringify(payload) }),

  listForClub: async (clubId: number | string) =>
    apiClient.request(`/coach-evaluations/club/${clubId}`),

  listForPlayer: async (playerId: number | string, clubId: number | string) =>
    apiClient.request(`/coach-evaluations/${playerId}?club_id=${clubId}`),

  delete: async (evaluationId: number | string) =>
    apiClient.request(`/coach-evaluations/${evaluationId}`, { method: 'DELETE' }),

  update: async (
    evaluationId: number | string,
    payload: {
      technical_skills?: number;
      movement?: number;
      tactical_ability?: number;
      physical_attributes?: number;
      psychological_factors?: number;
      quarter_plan?: string;
      comments?: string;
    },
  ) => apiClient.request(`/coach-evaluations/${evaluationId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
};
